import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  WorkflowsClient,
  AutomatedWorkflowDto,
  WorkflowTriggerDto,
  WorkflowConditionFieldDto,
  WorkflowActionDto,
  WorkflowConditionPairDto,
  WorkflowConditionCriteriaDto,
  WorkflowConditionLogic,
  WorkflowConditionOperator,
  WorkflowActionType,
  WorkflowTriggerType,
  WorkflowConditionField,
  CreateUpdateWorkflowDto,
  WorkflowUserDto,
  WorkflowRoleDto,
  WorkflowOrderTypeDto,
  WorkflowSupplierDto,
  WorkflowStockListDto,
  WorkflowZoneDto,
  WorkflowReportTypeDto,
  WorkflowTaskTemplateDto,
} from '../../app/services/api/workflowsClient';

const workflowsClient = new WorkflowsClient();

const operatorLabelDefaults: Record<WorkflowConditionOperator, string> = {
  'Equals': 'equals',
  'NotEquals': 'does not equal',
  'Contains': 'contains',
  'NotContains': 'does not contain',
  'GreaterThan': 'is greater than',
  'LessThan': 'is less than',
  'GreaterThanOrEquals': 'is greater than or equal to',
  'LessThanOrEquals': 'is less than or equal to',
  'IsEmpty': 'is empty',
  'IsNotEmpty': 'is not empty',
  'In': 'is one of',
  'NotIn': 'is not one of',
};

const operatorTranslationKeys: Record<WorkflowConditionOperator, string> = {
  'Equals': 'operatorEquals',
  'NotEquals': 'operatorNotEquals',
  'Contains': 'operatorContains',
  'NotContains': 'operatorNotContains',
  'GreaterThan': 'operatorGreaterThan',
  'LessThan': 'operatorLessThan',
  'GreaterThanOrEquals': 'operatorGreaterThanOrEquals',
  'LessThanOrEquals': 'operatorLessThanOrEquals',
  'IsEmpty': 'operatorIsEmpty',
  'IsNotEmpty': 'operatorIsNotEmpty',
  'In': 'operatorIn',
  'NotIn': 'operatorNotIn',
};

const getOperatorsForDataType = (dataType: string): WorkflowConditionOperator[] => {
  switch (dataType) {
    case 'number':
      return ['Equals', 'NotEquals', 'GreaterThan', 'LessThan', 'GreaterThanOrEquals', 'LessThanOrEquals'];
    case 'enum':
      return ['Equals', 'NotEquals', 'In', 'NotIn'];
    case 'boolean':
      return ['Equals', 'NotEquals'];
    default:
      return ['Equals', 'NotEquals', 'Contains', 'NotContains', 'IsEmpty', 'IsNotEmpty'];
  }
};

const AutomatedWorkflowDetails: React.FC = () => {
  const { t } = useTranslation('automatedWorkflows');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo, stores } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const canCreate = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsCreate', 'ConfigurationAdmin']) : true;
  const canEdit = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsEdit', 'ConfigurationAdmin']) : true;

  const [triggers, setTriggers] = useState<WorkflowTriggerDto[]>([]);
  const [conditionFields, setConditionFields] = useState<WorkflowConditionFieldDto[]>([]);
  const [actions, setActions] = useState<WorkflowActionDto[]>([]);
  const [workflow, setWorkflow] = useState<AutomatedWorkflowDto | null>(null);

  // Reference data for action parameter dropdowns
  const [users, setUsers] = useState<WorkflowUserDto[]>([]);
  const [roles, setRoles] = useState<WorkflowRoleDto[]>([]);
  const [orderTypes, setOrderTypes] = useState<WorkflowOrderTypeDto[]>([]);
  const [suppliers, setSuppliers] = useState<WorkflowSupplierDto[]>([]);
  const [stockLists, setStockLists] = useState<WorkflowStockListDto[]>([]);
  const [zones, setZones] = useState<WorkflowZoneDto[]>([]);
  const [reportTypes, setReportTypes] = useState<WorkflowReportTypeDto[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<WorkflowTaskTemplateDto[]>([]);

  const [formValues, setFormValues] = useState<{
    name: string;
    description: string;
    franchiseId: number;
    storeNumbers: string[];
    isActive: boolean;
    trigger: WorkflowTriggerType | '';
    conditionPair1: WorkflowConditionPairDto;
    conditionPair2: WorkflowConditionPairDto | null;
    conditionPairLogic: WorkflowConditionLogic;
    actionType: WorkflowActionType | '';
    actionParams: Record<string, string>;
  }>({
    name: '',
    description: '',
    franchiseId: 1,
    storeNumbers: [],
    isActive: true,
    trigger: '',
    conditionPair1: {
      id: 1,
      criteria1: { id: 1, field: 'TaskCategory', operator: 'Equals', value: '' },
      criteria2: null,
      criteriaLogic: 'AND',
    },
    conditionPair2: null,
    conditionPairLogic: 'AND',
    actionType: '',
    actionParams: {},
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadReferenceData();
  }, []);

  useEffect(() => {
    if (!isCreate && id) {
      loadWorkflow(Number(id));
    }
  }, [id, isCreate]);

  const loadReferenceData = async () => {
    try {
      const [
        triggersData,
        fieldsData,
        actionsData,
        usersData,
        rolesData,
        orderTypesData,
        suppliersData,
        stockListsData,
        zonesData,
        reportTypesData,
        taskTemplatesData,
      ] = await Promise.all([
        workflowsClient.getTriggers(),
        workflowsClient.getConditionFields(),
        workflowsClient.getActions(),
        workflowsClient.getUsers(),
        workflowsClient.getRoles(),
        workflowsClient.getOrderTypes(),
        workflowsClient.getSuppliers(),
        workflowsClient.getStockLists(),
        workflowsClient.getZones(),
        workflowsClient.getReportTypes(),
        workflowsClient.getTaskTemplates(),
      ]);
      setTriggers(triggersData);
      setConditionFields(fieldsData);
      setActions(actionsData);
      setUsers(usersData);
      setRoles(rolesData);
      setOrderTypes(orderTypesData);
      setSuppliers(suppliersData);
      setStockLists(stockListsData);
      setZones(zonesData);
      setReportTypes(reportTypesData);
      setTaskTemplates(taskTemplatesData);
    } catch {
      // Handle error
    }
  };

  const loadWorkflow = async (workflowId: number) => {
    try {
      const data = await workflowsClient.getWorkflow(workflowId);
      if (data) {
        setWorkflow(data);
        setFormValues({
          name: data.name,
          description: data.description,
          franchiseId: data.franchiseId,
          storeNumbers: data.storeNumbers ?? [],
          isActive: data.isActive,
          trigger: data.trigger,
          conditionPair1: data.conditionPair1,
          conditionPair2: data.conditionPair2 ?? null,
          conditionPairLogic: data.conditionPairLogic,
          actionType: data.action.type,
          actionParams: data.action.params,
        });
      }
    } catch {
      // Handle error
    }
  };

  if (isCreate && !canCreate) {
    return (
      <PageArea>
        <PageTitle>{t('detailsPageTitle', 'Automated Workflow')}</PageTitle>
        <Pill text={t('noPermissionCreate', 'You do not have permission to create workflows.')} />
      </PageArea>
    );
  }

  if (!isCreate && !canEdit) {
    return (
      <PageArea>
        <PageTitle>{t('detailsPageTitle', 'Automated Workflow')}</PageTitle>
        <Pill text={t('noPermissionEdit', 'You do not have permission to edit workflows.')} />
      </PageArea>
    );
  }

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.name.trim()) e.name = t('validationNameRequired', 'Name is required');
    if (!formValues.trigger) e.trigger = t('validationTriggerRequired', 'Trigger is required');
    if (!formValues.actionType) e.actionType = t('validationActionRequired', 'Action is required');
    if (!formValues.conditionPair1.criteria1.value && formValues.conditionPair1.criteria1.operator !== 'IsEmpty' && formValues.conditionPair1.criteria1.operator !== 'IsNotEmpty') {
      e['conditionPair1.criteria1.value'] = t('validationValueRequired', 'Value is required');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      const dto: CreateUpdateWorkflowDto = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        franchiseId: formValues.franchiseId,
        storeNumbers: formValues.storeNumbers.length > 0 ? formValues.storeNumbers : null,
        isActive: formValues.isActive,
        trigger: formValues.trigger as WorkflowTriggerType,
        conditionPair1: formValues.conditionPair1,
        conditionPair2: formValues.conditionPair2,
        conditionPairLogic: formValues.conditionPairLogic,
        action: {
          type: formValues.actionType as WorkflowActionType,
          params: formValues.actionParams,
        },
      };

      if (isCreate) {
        await workflowsClient.createWorkflow(dto);
      } else if (workflow) {
        await workflowsClient.updateWorkflow(workflow.id, dto);
      }

      navigate('/automated-workflows');
    } catch {
      // Handle error
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/automated-workflows');
  };

  const handleTriggerChange = (newTrigger: WorkflowTriggerType) => {
    // Get condition fields applicable to the new trigger
    const applicableFields = conditionFields.filter(f => {
      if (!f.applicableTriggers || f.applicableTriggers.length === 0) return true;
      return f.applicableTriggers.includes(newTrigger);
    });

    // Get the first applicable field, or fall back to the first available
    const defaultField = applicableFields.length > 0 ? applicableFields[0].field : 'StoreNumber';

    setFormValues(prev => ({
      ...prev,
      trigger: newTrigger,
      // Reset condition pair 1 with a field applicable to the new trigger
      conditionPair1: {
        ...prev.conditionPair1,
        criteria1: { ...prev.conditionPair1.criteria1, field: defaultField, value: '' },
        criteria2: prev.conditionPair1.criteria2
          ? { ...prev.conditionPair1.criteria2, field: defaultField, value: '' }
          : null,
      },
      // Reset condition pair 2 if it exists
      conditionPair2: prev.conditionPair2
        ? {
          ...prev.conditionPair2,
          criteria1: { ...prev.conditionPair2.criteria1, field: defaultField, value: '' },
          criteria2: prev.conditionPair2.criteria2
            ? { ...prev.conditionPair2.criteria2, field: defaultField, value: '' }
            : null,
        }
        : null,
    }));
  };

  const title = isCreate ? t('createTitle', 'Create Automated Workflow') : t('editTitle', 'Edit Automated Workflow');

  const getFieldInfo = (field: WorkflowConditionField) => {
    return conditionFields.find(f => f.field === field);
  };

  // Filter condition fields based on selected trigger
  const filteredConditionFields = conditionFields.filter(f => {
    // If no trigger is selected, show all fields
    if (!formValues.trigger) return true;
    // If field has no applicableTriggers, it applies to all triggers
    if (!f.applicableTriggers || f.applicableTriggers.length === 0) return true;
    // Otherwise, check if the selected trigger is in the applicable list
    return f.applicableTriggers.includes(formValues.trigger);
  });

  // Helper to render action parameter field (dropdown for IDs, text for others)
  const renderActionParamField = (param: string, required: boolean) => {
    // Map technical parameter names to user-friendly labels
    const friendlyLabels: Record<string, string> = {
      userId: t('actionParamLabelUser', 'User'),
      assignedUserId: t('actionParamLabelAssignedUser', 'Assigned User'),
      newAssignedUserId: t('actionParamLabelNewAssignedUser', 'New Assigned User'),
      roleId: t('actionParamLabelRole', 'Role'),
      assignedRoleId: t('actionParamLabelAssignedRole', 'Assigned Role'),
      taskTemplateId: t('actionParamLabelTaskTemplate', 'Task Template'),
      orderTypeId: t('actionParamLabelOrderType', 'Order Type'),
      supplierId: t('actionParamLabelSupplier', 'Supplier'),
      stockListId: t('actionParamLabelStockList', 'Stock List'),
      zoneId: t('actionParamLabelZone', 'Zone'),
      reportType: t('actionParamLabelReportType', 'Report Type'),
      priority: t('actionParamLabelPriority', 'Priority'),
      newPriority: t('actionParamLabelNewPriority', 'New Priority'),
    };
    const baseLabel = friendlyLabels[param] ?? param.replace(/([A-Z])/g, ' $1').replace(/\s*Id$/i, '').trim();
    const label = baseLabel + (required ? '' : ` ${t('optionalSuffix', '(optional)')}`);
    const value = formValues.actionParams[param] ?? '';
    const onChange = (newValue: string) => setFormValues(prev => ({
      ...prev,
      actionParams: { ...prev.actionParams, [param]: newValue },
    }));

    // User selection fields
    if (param.toLowerCase().includes('userid')) {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectUserPlaceholder', '-- Select User --')}</MenuItem>
          {users.map(u => (
            <MenuItem
              key={u.id}
              value={String(u.id)}>
              {u.name} ({u.email})
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Role selection fields
    if (param.toLowerCase().includes('roleid')) {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectRolePlaceholder', '-- Select Role --')}</MenuItem>
          {roles.map(r => (
            <MenuItem
              key={r.id}
              value={String(r.id)}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Task Template selection
    if (param === 'taskTemplateId') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectTaskTemplatePlaceholder', '-- Select Task Template --')}</MenuItem>
          {taskTemplates.map(t => (
            <MenuItem
              key={t.id}
              value={String(t.id)}>
              {t.name} ({t.category})
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Order Type selection
    if (param === 'orderTypeId') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectOrderTypePlaceholder', '-- Select Order Type --')}</MenuItem>
          {orderTypes.map(ot => (
            <MenuItem
              key={ot.id}
              value={String(ot.id)}>
              {ot.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Supplier selection
    if (param === 'supplierId') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectSupplierPlaceholder', '-- Select Supplier --')}</MenuItem>
          {suppliers.map(s => (
            <MenuItem
              key={s.id}
              value={String(s.id)}>
              {s.name} ({s.code})
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Stock List selection
    if (param === 'stockListId') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectStockListPlaceholder', '-- Select Stock List --')}</MenuItem>
          {stockLists.map(sl => (
            <MenuItem
              key={sl.id}
              value={String(sl.id)}>
              {sl.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Zone selection
    if (param === 'zoneId') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectZonePlaceholder', '-- Select Zone --')}</MenuItem>
          {zones.map(z => (
            <MenuItem
              key={z.id}
              value={String(z.id)}>
              {z.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Report Type
    if (param === 'reportType') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectReportTypePlaceholder', '-- Select Report Type --')}</MenuItem>
          {reportTypes.map(rt => (
            <MenuItem
              key={rt.id}
              value={rt.id}>
              {rt.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Priority dropdown
    if (param === 'priority' || param === 'newPriority') {
      return (
        <TextField
          select
          label={label}
          fullWidth
          required={required}
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <MenuItem value="">{t('selectPriorityPlaceholder', '-- Select Priority --')}</MenuItem>
          <MenuItem value="Low">{t('priorityLow', 'Low')}</MenuItem>
          <MenuItem value="Medium">{t('priorityMedium', 'Medium')}</MenuItem>
          <MenuItem value="High">{t('priorityHigh', 'High')}</MenuItem>
          <MenuItem value="Emergency">{t('priorityEmergency', 'Emergency')}</MenuItem>
        </TextField>
      );
    }

    // Default: text field
    return (
      <TextField
        label={label}
        fullWidth
        required={required}
        size="small"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    );
  };

  const updateCriteria = (
    pairKey: 'conditionPair1' | 'conditionPair2',
    criteriaKey: 'criteria1' | 'criteria2',
    updates: Partial<WorkflowConditionCriteriaDto>,
  ) => {
    setFormValues(prev => {
      const pair = pairKey === 'conditionPair1' ? prev.conditionPair1 : prev.conditionPair2;
      if (!pair) return prev;

      const criteria = criteriaKey === 'criteria1' ? pair.criteria1 : pair.criteria2;
      if (!criteria && criteriaKey === 'criteria2') {
        // Adding criteria2
        return {
          ...prev,
          [pairKey]: {
            ...pair,
            criteria2: { id: Date.now(), field: 'TaskCategory', operator: 'Equals', value: '', ...updates },
          },
        };
      }

      return {
        ...prev,
        [pairKey]: {
          ...pair,
          [criteriaKey]: { ...criteria!, ...updates },
        },
      };
    });
  };

  const removeCriteria2 = (pairKey: 'conditionPair1' | 'conditionPair2') => {
    setFormValues(prev => {
      const pair = pairKey === 'conditionPair1' ? prev.conditionPair1 : prev.conditionPair2;
      if (!pair) return prev;
      return {
        ...prev,
        [pairKey]: { ...pair, criteria2: null },
      };
    });
  };

  const addConditionPair2 = () => {
    setFormValues(prev => ({
      ...prev,
      conditionPair2: {
        id: Date.now(),
        criteria1: { id: Date.now(), field: 'TaskCategory', operator: 'Equals', value: '' },
        criteria2: null,
        criteriaLogic: 'AND',
      },
    }));
  };

  const removeConditionPair2 = () => {
    setFormValues(prev => ({ ...prev, conditionPair2: null }));
  };

  const renderCriteriaEditor = (
    pairKey: 'conditionPair1' | 'conditionPair2',
    criteriaKey: 'criteria1' | 'criteria2',
    criteria: WorkflowConditionCriteriaDto | null,
    showRemove: boolean,
  ) => {
    if (!criteria) return null;

    const fieldInfo = getFieldInfo(criteria.field);
    const operators: WorkflowConditionOperator[] = fieldInfo ? getOperatorsForDataType(fieldInfo.dataType) : ['Equals'];

    return (
      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}>
        <Grid item
          xs={12}
          md={3}>
          <TextField
            select
            label={t('fieldLabel', 'Field')}
            fullWidth
            size="small"
            value={criteria.field}
            onChange={e => updateCriteria(pairKey, criteriaKey, { field: e.target.value as WorkflowConditionField, value: '' })}
          >
            {filteredConditionFields.map(f => (
              <MenuItem key={f.id}
                value={f.field}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          md={3}>
          <TextField
            select
            label={t('operatorLabel', 'Operator')}
            fullWidth
            size="small"
            value={criteria.operator}
            onChange={e => updateCriteria(pairKey, criteriaKey, { operator: e.target.value as WorkflowConditionOperator })}
          >
            {operators.map(op => (
              <MenuItem key={op}
                value={op}>
                {t(operatorTranslationKeys[op], operatorLabelDefaults[op])}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          md={4}>
          {fieldInfo?.dataType === 'enum' && fieldInfo.enumValues ? (
            <TextField
              select
              label={t('valueLabel', 'Value')}
              fullWidth
              size="small"
              value={criteria.value}
              onChange={e => updateCriteria(pairKey, criteriaKey, { value: e.target.value })}
              error={!!errors[`${pairKey}.${criteriaKey}.value`]}
              helperText={errors[`${pairKey}.${criteriaKey}.value`]}
            >
              {fieldInfo.enumValues.map(v => (
                <MenuItem key={v}
                  value={v}>{v}</MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label={t('valueLabel', 'Value')}
              fullWidth
              size="small"
              value={criteria.value}
              onChange={e => updateCriteria(pairKey, criteriaKey, { value: e.target.value })}
              error={!!errors[`${pairKey}.${criteriaKey}.value`]}
              helperText={errors[`${pairKey}.${criteriaKey}.value`]}
              type={fieldInfo?.dataType === 'number' ? 'number' : 'text'}
              disabled={criteria.operator === 'IsEmpty' || criteria.operator === 'IsNotEmpty'}
            />
          )}
        </Grid>
        {showRemove && (
          <Grid item
            xs={12}
            md={2}>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeCriteria2(pairKey)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderConditionPair = (
    pairKey: 'conditionPair1' | 'conditionPair2',
    pair: WorkflowConditionPairDto,
    pairNumber: number,
    showRemovePair: boolean,
  ) => {
    return (
      <Box sx={{ mb: 3, p: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="textMD"
            fontWeight={500}
          >
            {t('conditionGroupLabel', 'Condition Group {{number}}', { number: pairNumber })}
          </Typography>
          {showRemovePair && (
            <IconButton size="small"
              color="error"
              onClick={removeConditionPair2}>
              <DeleteOutlineIcon />
            </IconButton>
          )}
        </Box>

        {renderCriteriaEditor(pairKey, 'criteria1', pair.criteria1, false)}

        {pair.criteria2 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                select
                size="small"
                value={pair.criteriaLogic}
                onChange={e => setFormValues(prev => ({
                  ...prev,
                  [pairKey]: { ...pair, criteriaLogic: e.target.value as WorkflowConditionLogic },
                }))}
                sx={{ width: 100 }}
              >
                <MenuItem value="AND">{t('logicAnd', 'AND')}</MenuItem>
                <MenuItem value="OR">{t('logicOr', 'OR')}</MenuItem>
              </TextField>
            </Box>
            {renderCriteriaEditor(pairKey, 'criteria2', pair.criteria2, true)}
          </>
        )}

        {!pair.criteria2 && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => updateCriteria(pairKey, 'criteria2', {})}
          >
            {t('addCriteria', 'Add Criteria')}
          </Button>
        )}
      </Box>
    );
  };

  const selectedAction = actions.find(a => a.type === formValues.actionType);

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <PageTitle subTitle={t('detailsSubtitle', 'Configure trigger, conditions, and action for this workflow')}>
              {title}
            </PageTitle>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isSaving}
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button variant="primary"
                onClick={handleSave}
                disabled={isSaving}>
                {isSaving ? t('saving', 'Saving...') : isCreate ? t('createWorkflow', 'Create Workflow') : t('saveChanges', 'Save Changes')}
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Basic Information */}
        <Grid item
          xs={12}>
          <Section style={{ p: 8, pt: 2 }}>
            <TableHeader>{t('basicInfoTitle', 'Basic Information')}</TableHeader>
            <Box>
              <Grid container
                spacing={3}>
                <Grid item
                  xs={12}
                  md={6}>
                  <TextField
                    label={t('workflowNameLabel', 'Workflow Name')}
                    fullWidth
                    required
                    value={formValues.name}
                    onChange={e => setFormValues(prev => ({ ...prev, name: e.target.value }))}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formValues.isActive}
                        onChange={e => setFormValues(prev => ({ ...prev, isActive: e.target.checked }))}
                      />
                    }
                    label={t('active', 'Active')}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('descriptionLabel', 'Description')}
                    fullWidth
                    multiline
                    rows={2}
                    value={formValues.description}
                    onChange={e => setFormValues(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={6}>
                  <Autocomplete
                    multiple
                    options={stores?.map(s => s.storeNumber) ?? []}
                    value={formValues.storeNumbers}
                    onChange={(_, value) => setFormValues(prev => ({ ...prev, storeNumbers: value as string[] }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('storesLabel', 'Stores (leave empty for all stores)')}
                        placeholder={t('storesPlaceholder', 'Select stores')}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Section>
        </Grid>

        {/* Trigger */}
        <Grid item
          xs={12}>
          <Section style={{ p: 8, pt: 2 }}>
            <TableHeader>{t('triggerSectionTitle', 'When this happens... (Trigger)')}</TableHeader>
            <Box>
              <Grid container
                spacing={3}>
                <Grid item
                  xs={12}
                  md={6}>
                  <TextField
                    select
                    label={t('triggerEventLabel', 'Trigger Event')}
                    fullWidth
                    required
                    value={formValues.trigger}
                    onChange={e => handleTriggerChange(e.target.value as WorkflowTriggerType)}
                    error={!!errors.trigger}
                    helperText={errors.trigger || t('triggerHelperText', 'Select the event that will trigger this workflow')}
                  >
                    {Object.entries(
                      triggers.reduce((acc, t) => {
                        if (!acc[t.category]) acc[t.category] = [];
                        acc[t.category].push(t);
                        return acc;
                      }, {} as Record<string, WorkflowTriggerDto[]>),
                    ).map(([category, categoryTriggers]) => [
                      <MenuItem key={`cat-${category}`}
                        disabled
                        sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {category}
                      </MenuItem>,
                      ...categoryTriggers.map(t => (
                        <MenuItem key={t.type}
                          value={t.type}
                          sx={{ pl: 4 }}>
                          {t.name}
                        </MenuItem>
                      )),
                    ])}
                  </TextField>
                </Grid>
                {formValues.trigger && (
                  <Grid item
                    xs={12}
                    md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography
                        variant="textSM"
                        color="text.secondary"
                      >
                        {triggers.find(t => t.type === formValues.trigger)?.description}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Conditions */}
            <Grid item
              xs={12}>
              <Section style={{ p: 8, pt: 4, boxShadow: 'none' }}>
                <TableHeader>{t('conditionsSectionTitle', 'If these conditions are met... (Conditions)')}</TableHeader>
                <Box sx={{ p: 3 }}>
                  <Typography
                    variant="textSM"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('conditionsDescription', 'Define up to 2 condition groups, each with up to 2 criteria. The workflow will only execute if all conditions are met.')}
                  </Typography>

                  {renderConditionPair('conditionPair1', formValues.conditionPair1, 1, false)}

                  {formValues.conditionPair2 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2, p: 4 }}>
                        <Divider sx={{ flex: 1 }} />
                        <TextField
                          select
                          size="small"
                          value={formValues.conditionPairLogic}
                          onChange={e => setFormValues(prev => ({ ...prev, conditionPairLogic: e.target.value as WorkflowConditionLogic }))}
                          sx={{ width: 100 }}
                        >
                          <MenuItem value="AND">{t('logicAnd', 'AND')}</MenuItem>
                          <MenuItem value="OR">{t('logicOr', 'OR')}</MenuItem>
                        </TextField>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                      {renderConditionPair('conditionPair2', formValues.conditionPair2, 2, true)}
                    </>
                  )}

                  {!formValues.conditionPair2 && (
                    <Button
                      variant="secondary"
                      startIcon={<AddIcon />}
                      onClick={addConditionPair2}
                    >
                      {t('addConditionGroup', 'Add Condition Group')}
                    </Button>
                  )}
                </Box>
              </Section>
            </Grid>

            {/* Action */}

            <TableHeader>{t('actionSectionTitle', 'Then do this... (Action)')}</TableHeader>
            <Box sx={{ p: 3 }}>
              <Grid container
                spacing={3}>
                <Grid item
                  xs={12}
                  md={6}>
                  <TextField
                    select
                    label={t('actionLabel', 'Action')}
                    fullWidth
                    required
                    value={formValues.actionType}
                    onChange={e => setFormValues(prev => ({ ...prev, actionType: e.target.value as WorkflowActionType, actionParams: {} }))}
                    error={!!errors.actionType}
                    helperText={errors.actionType || t('actionHelperText', 'Select the action to perform when conditions are met')}
                  >
                    {Object.entries(
                      actions.reduce((acc, a) => {
                        if (!acc[a.category]) acc[a.category] = [];
                        acc[a.category].push(a);
                        return acc;
                      }, {} as Record<string, WorkflowActionDto[]>),
                    ).map(([category, categoryActions]) => [
                      <MenuItem key={`cat-${category}`}
                        disabled
                        sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {category}
                      </MenuItem>,
                      ...categoryActions.map(a => (
                        <MenuItem key={a.type}
                          value={a.type}
                          sx={{ pl: 4 }}>
                          {a.name}
                        </MenuItem>
                      )),
                    ])}
                  </TextField>
                </Grid>
                {selectedAction && (
                  <Grid item
                    xs={12}
                    md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography
                        variant="textSM"
                        color="text.secondary"
                      >
                        {selectedAction.description}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Action Parameters */}
                {selectedAction && (
                  <Grid item
                    xs={12}>
                    <Typography
                      variant="textMD"
                    >
                      {t('actionParametersTitle', 'Action Parameters')}
                    </Typography>
                    <Grid container
                      spacing={2}

                      sx={{ mt: 2, mb: 2 }}>
                      {selectedAction.requiredParams.map(param => (
                        <Grid item
                          xs={12}
                          md={4}
                          key={param}>
                          {renderActionParamField(param, true)}
                        </Grid>
                      ))}
                      {selectedAction.optionalParams.map(param => (
                        <Grid item
                          xs={12}
                          md={4}
                          key={param}>
                          {renderActionParamField(param, false)}
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Section>
        </Grid>
      </Grid>
    </PageArea >
  );
};

export default AutomatedWorkflowDetails;
